import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { MapPin, Search, ArrowRight, Shield, Truck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import ItemCard from "@/components/ItemCard";
import SEOHead, { generateBreadcrumbSchema, generateItemListSchema } from "@/components/SEOHead";
import { CATEGORIES, LOCATION_SLUG_MAP, locationToSlug, CARIBBEAN_LOCATIONS, US_STATES, getCaribbeanBySlug, getUSCityRedirect } from "@/lib/constants";
import { SEO_BORROW_CATEGORIES } from "@/lib/citySEO";
import api from "@/lib/api";
import StatePage from "@/pages/StatePage";

// SEO content per Caribbean location — borrow/lend only, no rent/rental/sharing economy
const CARIBBEAN_SEO = {
  "Cayman Islands": {
    headline: "Borrow Items from Locals in the Cayman Islands",
    intro: "Paradise Borrowed\u2122 connects you with trusted lenders in the Cayman Islands \u2014 from Seven Mile Beach and George Town to Rum Point and the Sister Islands. Whether you\u2019re diving the Kittiwake wreck, relaxing at a beachfront villa, or exploring Stingray City, borrow the gear you need from island locals instead of packing heavy luggage.",
    geo: "The Cayman Islands, a British Overseas Territory in the western Caribbean, welcome over two million visitors annually. Grand Cayman, Cayman Brac, and Little Cayman offer world-class diving, pristine beaches, and a thriving community of residents and expats.",
    metaDesc: "Borrow beach gear, baby equipment, cameras, tools & more from locals in the Cayman Islands. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Grand Cayman and beyond.",
    popularSearches: ["borrow car seat Cayman Islands", "borrow surfboard Grand Cayman", "borrow baby stroller Cayman", "borrow snorkel gear Seven Mile Beach", "borrow camera Cayman Islands"],
  },
  "Turks and Caicos": {
    headline: "Borrow Items from Locals in Turks and Caicos",
    intro: "Paradise Borrowed\u2122 serves Turks and Caicos \u2014 home to the world-famous Grace Bay Beach on Providenciales. Whether you\u2019re snorkeling at Smith\u2019s Reef, exploring Chalk Sound, or enjoying a family vacation at Long Bay, borrow cameras, water gear, baby equipment, and more from local lenders instead of buying disposable tourist items.",
    geo: "Turks and Caicos, a British Overseas Territory south of the Bahamas, is one of the Caribbean\u2019s fastest-growing destinations. Providenciales (Provo) is the main tourist hub, with direct flights from major U.S. cities.",
    metaDesc: "Borrow beach gear, baby equipment, cameras & more from locals in Turks and Caicos. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Providenciales.",
    popularSearches: ["borrow snorkel gear Turks and Caicos", "borrow baby gear Providenciales", "borrow camera Grace Bay", "borrow paddleboard TCI", "borrow beach gear Turks and Caicos"],
  },
  "British Virgin Islands": {
    headline: "Borrow Items from Locals in the British Virgin Islands",
    intro: "Paradise Borrowed\u2122 connects borrowers and lenders across the British Virgin Islands \u2014 from Tortola and Virgin Gorda to Jost Van Dyke and Anegada. Whether you\u2019re chartering a yacht through the islands or staying at a beachfront villa, borrow water gear, baby equipment, cameras, and more from BVI locals.",
    geo: "The British Virgin Islands, an archipelago of over 60 islands east of Puerto Rico, are a world-renowned sailing destination. Tortola is the largest island and main port of entry.",
    metaDesc: "Borrow water gear, baby equipment, cameras & more from locals in the British Virgin Islands. Paradise Borrowed\u2122 \u2014 borrowing in Tortola & Virgin Gorda.",
    popularSearches: ["borrow water gear BVI", "borrow baby car seat Tortola", "borrow snorkel gear Virgin Gorda", "borrow beach gear British Virgin Islands", "borrow camera BVI"],
  },
  "Bermuda": {
    headline: "Borrow Items from Locals in Bermuda",
    intro: "Paradise Borrowed\u2122 serves Bermuda \u2014 the pink-sand island in the North Atlantic, just two hours from the U.S. East Coast. Whether you\u2019re exploring Hamilton\u2019s shops, swimming at Horseshoe Bay, or cycling the Railway Trail, borrow the items you need from Bermuda\u2019s welcoming local community.",
    geo: "Bermuda, a self-governing British Overseas Territory, sits 650 miles east of North Carolina. Its compact size, reliable transit, and walkable towns make it an ideal community for peer-to-peer borrowing.",
    metaDesc: "Borrow beach gear, baby equipment, cameras & more from locals in Bermuda. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Hamilton & Horseshoe Bay.",
    popularSearches: ["borrow beach gear Bermuda", "borrow baby stroller Bermuda", "borrow snorkel gear Horseshoe Bay", "borrow camera Bermuda", "borrow bike Bermuda"],
  },
  "St. Barts": {
    headline: "Borrow Items from Locals in St. Barts",
    intro: "Paradise Borrowed\u2122 connects you with lenders in St. Barts \u2014 the jewel of the French Caribbean, known for Gustavia\u2019s harbor, Shell Beach, and Colombier Bay. Whether you\u2019re arriving by yacht or plane, borrow beach gear, cameras, baby equipment, and more from island locals instead of paying boutique resort prices.",
    geo: "St. Barth\u00e9lemy (St. Barts), a French overseas collectivity, is one of the Caribbean\u2019s most exclusive destinations. Despite its small size, the island welcomes a steady stream of visitors year-round.",
    metaDesc: "Borrow beach gear, cameras, baby equipment & more from locals in St. Barts. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Gustavia & beyond.",
    popularSearches: ["borrow beach gear St Barts", "borrow water gear St Barthelemy", "borrow baby car seat St Barts", "borrow camera St Barts", "borrow paddleboard St Barts"],
  },
  "Aruba": {
    headline: "Borrow Items from Locals in Aruba",
    intro: "Paradise Borrowed\u2122 serves Aruba \u2014 the One Happy Island, famous for Eagle Beach, Arikok National Park, and year-round sunshine. Whether you\u2019re windsurfing at Hadicurari, snorkeling at Mangel Halto, or exploring the Natural Pool, borrow gear from Aruba\u2019s local community instead of overpaying at tourist shops.",
    geo: "Aruba, a constituent country of the Kingdom of the Netherlands, sits just 15 miles north of Venezuela. Its dry climate, constant trade winds, and modern infrastructure make it one of the most-visited Caribbean islands.",
    metaDesc: "Borrow beach gear, snorkel sets, baby equipment & more from locals in Aruba. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing on the One Happy Island.",
    popularSearches: ["borrow snorkel gear Aruba", "borrow beach chairs Aruba", "borrow baby car seat Aruba", "borrow surfboard Eagle Beach", "borrow camera Aruba"],
  },
  "Barbados": {
    headline: "Borrow Items from Locals in Barbados",
    intro: "Paradise Borrowed\u2122 connects borrowers and lenders in Barbados \u2014 from the surf breaks of Bathsheba and the nightlife of St. Lawrence Gap to the calm waters of the West Coast. Borrow surfboards, baby gear, cameras, and more from Bajan locals who know what visitors need.",
    geo: "Barbados, the easternmost Caribbean island, is an independent nation known for its Crop Over festival, rum heritage, and UNESCO-listed Bridgetown. Direct flights connect Barbados to major cities across North America and Europe.",
    metaDesc: "Borrow surfboards, baby gear, cameras & more from locals in Barbados. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Bridgetown & beyond.",
    popularSearches: ["borrow surfboard Barbados", "borrow baby equipment Barbados", "borrow beach gear Barbados", "borrow camera Bridgetown", "borrow snorkel gear Barbados"],
  },
  "Bahamas": {
    headline: "Borrow Items from Locals in the Bahamas",
    intro: "Paradise Borrowed\u2122 serves the Bahamas \u2014 an archipelago of 700 islands stretching from Nassau and Paradise Island to the Exumas, Eleuthera, and the Abacos. Whether you\u2019re island-hopping, swimming with the famous Exuma pigs, or enjoying Atlantis with the family, borrow what you need from Bahamian locals.",
    geo: "The Commonwealth of the Bahamas, located southeast of Florida, is one of the Caribbean\u2019s most popular destinations. Nassau on New Providence Island is the capital and main gateway, with frequent flights from U.S. cities.",
    metaDesc: "Borrow water gear, baby equipment, cameras & more from locals in the Bahamas. Paradise Borrowed\u2122 \u2014 peer-to-peer borrowing in Nassau & the islands.",
    popularSearches: ["borrow water gear Bahamas", "borrow baby car seat Nassau", "borrow beach gear Paradise Island", "borrow snorkel gear Bahamas", "borrow camera Bahamas"],
  },
};

export default function LocationPage() {
  const { locationSlug } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const redirectPath = useMemo(() => getUSCityRedirect(locationSlug), [locationSlug]);
  const isStatePage = useMemo(() => !!US_STATES[locationSlug], [locationSlug]);
  const location = useMemo(() => {
    if (isStatePage || redirectPath) return null;
    return LOCATION_SLUG_MAP[locationSlug] || getCaribbeanBySlug(locationSlug);
  }, [locationSlug, isStatePage, redirectPath]);

  const seo = useMemo(() => {
    if (!location) return null;
    return CARIBBEAN_SEO[location.name] || null;
  }, [location]);

  useEffect(() => {
    if (isStatePage || !location || redirectPath) return;
    api.get("/items", { params: { location: location.name, limit: 50 } })
      .then((res) => setItems(res.data.items))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [locationSlug, location, isStatePage, redirectPath]);

  if (redirectPath) return <Navigate to={redirectPath} replace />;
  if (isStatePage) return <StatePage />;

  if (!location) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading font-bold text-2xl text-stone-900 mb-2">Location Not Found</h1>
        <p className="text-stone-500 mb-6">We couldn't find this location.</p>
        <Link to="/"><Button className="rounded-full bg-coral hover:bg-coral text-white">Back to Home</Button></Link>
      </div>
    );
  }

  const isCaribbean = location.type === "caribbean";
  const locationImage = location.image || "https://images.unsplash.com/photo-1682289571606-0736333cb3eb?w=1200";
  const itemCategories = [...new Set(items.map((i) => i.category))];

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(searchQuery)}`);
  };

  // SEO data
  const pageTitle = `Borrow Items in ${location.name} | Paradise Borrowed\u2122`;
  const pageDescription = seo?.metaDesc || `Borrow beach gear, baby equipment, cameras, tools & more from locals in ${location.name}. Paradise Borrowed\u2122 \u2014 the peer-to-peer borrowing platform. Join free.`;
  const canonicalUrl = `https://www.paradiseborrowed.com/${locationSlug}`;
  const countryCode = isCaribbean ? (location.name === "Bermuda" ? "BM" : location.name === "Aruba" ? "AW" : location.name === "Barbados" ? "BB" : location.name === "Bahamas" ? "BS" : location.name === "Cayman Islands" ? "KY" : location.name === "Turks and Caicos" ? "TC" : location.name === "British Virgin Islands" ? "VG" : "FR") : "US";

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": `Borrow Items in ${location.name}`,
        "description": pageDescription,
        "url": canonicalUrl,
        "isPartOf": { "@type": "WebSite", "name": "Paradise Borrowed", "url": "https://www.paradiseborrowed.com" },
        "about": {
          "@type": "Place",
          "name": location.name,
          "address": { "@type": "PostalAddress", "addressLocality": location.name, "addressCountry": countryCode }
        }
      },
      generateBreadcrumbSchema([
        { name: "Home", url: "https://www.paradiseborrowed.com" },
        { name: location.name, url: canonicalUrl }
      ]),
      ...(items.length > 0 ? [generateItemListSchema(items, location.name)] : [])
    ]
  };

  // Sibling Caribbean destinations for internal linking
  const siblingDestinations = CARIBBEAN_LOCATIONS.filter(l => l.name !== location.name);

  return (
    <div data-testid="location-page">
      <SEOHead title={pageTitle} description={pageDescription} canonicalUrl={canonicalUrl} schema={schema} />

      {/* Hero */}
      {isCaribbean ? (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={locationImage} alt={location.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-white/80 mb-3">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">{location.name}</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight" data-testid="location-headline">
                {seo?.headline || `Borrow Items from Locals in ${location.name}`}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">{seo?.intro || pageDescription}</p>
              <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xl">{seo?.geo}</p>
              <form onSubmit={handleSearch} className="mt-8 max-w-lg" data-testid="location-search-form">
                <div className="flex items-center bg-white rounded-full p-1.5 shadow-xl">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <input type="text" placeholder={`Search items to borrow in ${location.name}...`}
                      value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 rounded-full text-sm bg-transparent focus:outline-none"
                      data-testid="location-search-input" />
                  </div>
                  <Button type="submit" className="rounded-full px-6 h-10 bg-coral hover:bg-coral text-white" data-testid="location-search-btn">Search</Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-gradient-to-br from-stone-50 via-orange-50/30 to-stone-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-stone-500 mb-3">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{location.name}</span>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-stone-900 leading-tight" data-testid="location-headline">
              {seo?.headline || `Borrow Items from Locals in ${location.name}`}
            </h1>
            <p className="mt-4 text-base text-stone-600 leading-relaxed max-w-2xl">{seo?.intro || pageDescription}</p>
            <form onSubmit={handleSearch} className="mt-8 max-w-lg" data-testid="location-search-form">
              <div className="flex items-center bg-white rounded-full border border-stone-200 p-1.5 shadow-sm hover:shadow-md transition-shadow">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" placeholder={`Search items to borrow in ${location.name}...`}
                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-full text-sm bg-transparent focus:outline-none"
                    data-testid="location-search-input" />
                </div>
                <Button type="submit" className="rounded-full px-6 h-10 bg-coral hover:bg-coral text-white" data-testid="location-search-btn">Search</Button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* What You Can Borrow */}
      <section className="py-12 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-6">
            What You Can Borrow in {location.name}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SEO_BORROW_CATEGORIES.map((cat) => (
              <Link key={cat.name}
                to={`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(cat.name)}`}
                className="p-4 rounded-xl border bg-white border-stone-200 hover:border-coral hover:shadow-sm text-left transition-all group"
                data-testid={`loc-category-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="font-medium text-sm mb-1 text-stone-900 group-hover:text-coral transition-colors">{cat.name}</p>
                <p className="text-xs text-stone-400">{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Available Items */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900">Available Items in {location.name}</h2>
            <Link to={`/browse?location=${encodeURIComponent(location.name)}`} className="text-coral text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-location-items">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl border border-stone-200 bg-white overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-stone-100" />
                  <div className="p-4 space-y-2"><div className="h-3 bg-stone-100 rounded w-1/2" /><div className="h-4 bg-stone-100 rounded w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => <ItemCard key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
              <p className="text-stone-500 text-lg mb-2">No items listed in {location.name} yet</p>
              <p className="text-stone-400 text-sm mb-4">Be the first lender in this area!</p>
              <Link to="/create-listing">
                <Button className="rounded-full bg-coral hover:bg-coral text-white" data-testid="list-first-item">List an Item</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* How Borrowing Works */}
      <section className="py-12 sm:py-16 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-8">How Borrowing Works</h2>
            <div className="space-y-0">
              {[
                { num: "1", title: `Browse items listed by lenders in ${location.name}`, desc: `Search by category or keyword. Every listing includes photos, condition details, and verified lender reviews. Filter by availability and delivery options in the ${location.name} area.` },
                { num: "2", title: "Book the dates you need", desc: "Select your borrowing dates, review the price breakdown (including any applicable platform fees), and pay securely through Stripe." },
                { num: "3", title: "Pick up, use, and return", desc: "Coordinate pickup or delivery with the lender through in-app messaging. Use the item, return it in the same condition, and confirm the return in the app." },
              ].map((step, i) => (
                <div key={step.num} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-coral text-white flex items-center justify-center text-sm font-semibold shrink-0">{step.num}</div>
                    {i < 2 && <div className="w-px flex-1 bg-stone-200 my-2"></div>}
                  </div>
                  <div className={i < 2 ? "pb-8" : ""}>
                    <p className="font-medium text-stone-900 mb-1">{step.title}</p>
                    <p className="text-sm text-stone-500 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Borrow Instead of Buy? */}
      <section className="py-12 sm:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-4">Why Borrow Instead of Buy?</h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-2">
              {isCaribbean
                ? `Traveling to ${location.name} means you can\u2019t bring everything. The borrowers economy solves this: access what you need from locals who already have it. A baby car seat, a surfboard, a DSLR camera \u2014 borrow it for a fraction of the purchase price.`
                : `The borrowers economy is simple: access what you need, when you need it, without the cost and waste of buying new. In ${location.name}, your neighbors already own the items you\u2019re looking for.`
              }
            </p>
            <p className="text-stone-600 text-sm leading-relaxed mb-8">
              Peer-to-peer borrowing through Paradise Borrowed saves you money, reduces waste, and builds community. Lenders earn from items that would otherwise sit idle. Borrowers get what they need at a fraction of the purchase price.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Truck, title: isCaribbean ? "Delivered to You" : "Local Pickup or Delivery", desc: isCaribbean ? `Many lenders in ${location.name} offer delivery right to your hotel, villa, or vacation home.` : `Most items in ${location.name} are available for local pickup. Many lenders offer delivery too.` },
                { icon: Shield, title: "Trusted Lenders", desc: "Every lender is reviewed and rated by previous borrowers. Verified profiles and in-app messaging keep every transaction transparent." },
                { icon: Clock, title: "Flexible Borrowing", desc: isCaribbean ? "Planning a trip? Reserve items weeks ahead so they\u2019re ready when you arrive. Borrow for a day, a week, or your entire stay." : "Borrow for a day, a week, or longer. Choose the dates that work for you and only pay for the time you need." },
              ].map((card) => (
                <div key={card.title} className="p-5 rounded-2xl bg-white border border-stone-200">
                  <card.icon className="w-6 h-6 text-coral mb-3" />
                  <h3 className="font-heading font-semibold text-sm text-stone-900 mb-1">{card.title}</h3>
                  <p className="text-xs text-stone-500 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Searches */}
      <section className="py-12 border-t border-stone-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading font-semibold text-base text-stone-900 mb-4">Popular Searches in {location.name}</h3>
          <div className="flex flex-wrap gap-2">
            {(seo?.popularSearches || [`borrow items ${location.name}`, `borrow beach gear ${location.name}`, `borrow camera ${location.name}`, `borrow baby gear ${location.name}`, `lend items ${location.name}`]).map((term) => (
              <Link key={term}
                to={`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(term.split(" ").slice(1, 3).join(" "))}`}
                className="text-xs px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-stone-600 hover:border-coral hover:text-coral transition-all"
              >
                {term}
              </Link>
            ))}
            {CATEGORIES.slice(0, 6).map((cat) => (
              <Link key={cat.name}
                to={`/browse?location=${encodeURIComponent(location.name)}&category=${encodeURIComponent(cat.name)}`}
                className="text-xs px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-stone-600 hover:border-coral hover:text-coral transition-all"
              >
                {cat.name} in {location.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Linking: Other Caribbean Destinations */}
      {isCaribbean && (
        <section className="py-12 sm:py-16 bg-emerald-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-white mb-2">
              Borrow Items in Other Caribbean Destinations
            </h2>
            <p className="text-sm text-white/60 mb-6">Paradise Borrowed is available across the Caribbean:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {siblingDestinations.map((loc) => (
                <Link key={loc.name} to={`/${locationToSlug(loc.name)}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[3/2]"
                  data-testid={`other-dest-${locationToSlug(loc.name)}`}
                >
                  <img src={loc.image} alt={`Borrow items in ${loc.name}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-3">
                    <span className="font-heading font-semibold text-sm text-white">Borrow items in {loc.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-50 via-amber-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900 mb-3">
            Start Borrowing in {location.name}
          </h2>
          <p className="text-stone-600 text-sm max-w-md mx-auto mb-6">
            Join Paradise Borrowed for free. Browse items from local lenders or list your own to start earning.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to={`/browse?location=${encodeURIComponent(location.name)}`}>
              <Button size="lg" className="rounded-full px-8 bg-coral hover:bg-coral text-white btn-press" data-testid="location-cta-browse">
                Browse Items in {location.name}
              </Button>
            </Link>
            <Link to="/create-listing">
              <Button size="lg" variant="outline" className="rounded-full px-8 border-stone-300 text-stone-700 hover:border-coral hover:text-coral" data-testid="location-cta-lend">
                Start Lending
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
